import React, { memo, useCallback, useMemo, useState } from 'react';
import AutoComplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import _ from 'lodash';

import { TextField } from 'ui/components/TextField/TextField';
import { fetchItem, Item } from 'services/items';
import { Pagination } from 'services/search';
import { initialPagination } from 'ui/components/Table/ItemsTable';
import { PaginationDirection } from 'services/url';

import { ItemsAutocompleteProps } from './types';
import NewItemModal from './NewItemModal';
import {
  itemsAutocompleteFilterOptions,
  itemsAutocompleteGetOptionLabel,
  resolvedFetchItemsFunction,
} from './helpers';
import { useAsyncAutocomplete, useScrollPosition } from '../Autocomplete/hooks';

const initialItemPagination: Pagination = {
  ...initialPagination,
  pageSize: 50,
  sort: { sortBy: 'name', direction: PaginationDirection.Ascending },
};

const ItemsAutocomplete: React.FC<ItemsAutocompleteProps> = (props) => {
  const {
    label,
    value,
    placeholder,
    removedIds = [],
    required,
    onChange,
    disableAdd = false,
    additionalInputProps,
    itemTypes = [],
    dataQa,
    error,
    expands,
    renderOption,
    customQuickSearchColumns,
    onlySaleItems,
  } = props;

  const {
    data: items,
    isLoading,
    innerValue,
    open,
    onCloseAutocomplete,
    onNextPage,
    onOpenAutocomplete,
    setSearchValue,
    setInnerValue,
    setData,
  } = useAsyncAutocomplete(
    value,
    resolvedFetchItemsFunction(
      itemTypes,
      expands,
      customQuickSearchColumns,
      onlySaleItems
    ),
    fetchItem,
    initialItemPagination
  );

  const [newItemName, setNewItemName] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { listRef, captureScrollPosition } = useScrollPosition(items);

  const filteredItems = useMemo(
    () => items.filter((item) => !removedIds.includes(item.id!)),
    [items, removedIds]
  );

  const handleAutocompleteInputChange = (
    e: any,
    val: string,
    reason: string
  ) => {
    // reason why this event is triggered
    // it can be 'input', 'clear' and 'reset'
    if (reason === 'input') {
      if (!val) {
        onOpenAutocomplete();
        setInnerValue(null);
      }
      setSearchValue(val || null);
      onOpenAutocomplete();
      return;
    }

    if (reason === 'clear') {
      onChange(null);
    }

    setSearchValue(null);
  };

  const handleChange = (e: any, item: Item | null) => {
    if (item && (item.id === null || item.id < 0)) {
      setNewItemName(item.name);
      setShowModal(true);
      return;
    }

    setInnerValue(item);
    onChange(item);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSaveModal = useCallback(
    async (item: Item) => {
      setShowModal(false);
      setData([item]);
      setInnerValue(item);
      onChange(item);
    },
    [onChange, setData, setInnerValue]
  );

  return (
    <>
      <AutoComplete
        className="redesign"
        options={filteredItems}
        open={open}
        onOpen={onOpenAutocomplete}
        onClose={onCloseAutocomplete}
        onChange={handleChange}
        filterOptions={itemsAutocompleteFilterOptions(disableAdd)}
        onInputChange={handleAutocompleteInputChange}
        getOptionLabel={itemsAutocompleteGetOptionLabel}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderOption={renderOption}
        value={innerValue}
        noOptionsText={isLoading ? 'Loading...' : 'No options'}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const node = event.currentTarget;
            if (
              Math.ceil(node.scrollTop + node.clientHeight) ===
              node.scrollHeight
            ) {
              onNextPage();
            }
          },
          // @ts-ignore
          ref: listRef,
          onScrollCapture: captureScrollPosition,
        }}
        renderInput={(params) => {
          // params.InputProps.endAdornment is component with 2 children
          // first child is clear button, so we want to set it to null
          const endAdornment = params.InputProps.endAdornment as any;

          return (
            <TextField
              // we are omitting props that would overwrite our styling in TextField
              {..._.omit(params, 'variant', 'size', 'InputLabelProps')}
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              InputProps={{
                ...params.InputProps,
                ...additionalInputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                'data-qa': dataQa,
                ...params.inputProps,
              }}
            />
          );
        }}
        clearIcon={
          <CloseIcon
            fontSize="small"
            data-qa={`${dataQa}-autocomplete-clear-icon`}
          />
        }
        popupIcon={
          <ArrowDropDownIcon
            fontSize="small"
            data-qa={`${dataQa}-autocomplete-dropdown-icon`}
          />
        }
      />
      <NewItemModal
        show={showModal}
        newItemName={newItemName}
        itemTypes={itemTypes}
        onSave={handleSaveModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default memo(ItemsAutocomplete);
